import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import FormEnvelopes from "@/components/tabs/FormEnvelopeTab";
import FormURLsCard from "@/components/tabs/FormUrlTab";
import TransferOwnershipButton from "@/components/buttons/TransferOwnershipButton";
import TransferOwnershipModal from "@/components/modals/TransferOwnershipModal";
import { DetailHeaderSkeleton, DetailTabsSkeleton } from "@/components/skeletons/shared";
import { getFormById, transferFormOwnership } from "@/services/form.service";
import { GLOBAL_ORGANIZATION, HEADINGS, LABELS } from "@/utils/constants/appConstants/commonConstants";
import { isFeaturePermitted } from "@/utils/functions/rbac";
import { getCurrentOrganization } from "@/utils/functions/OrganizationFunctions";

const FORM_DETAIL_TAB_WIDTHS = ["5.5rem", "3rem"] as const;

const FormDetails = () => {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [isTransferFormOwnershipPermitted, setIsTransferFormOwnershipPermitted] = useState(false);
    const [form, setForm] = useState<any>(null);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [transferLoading, setTransferLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<"envelopes" | "urls">("envelopes");
    const currentOrganization = getCurrentOrganization();

    useEffect(() => {
        if (!id) {
            return;
        }

        setIsTransferFormOwnershipPermitted(isFeaturePermitted("AdminPortalTransferFormOwnership"));
        setLoading(true);
        getFormById(
            id,
            (res: any) => {
                setForm(res);
                setLoading(false);
            },
            (err: any) => {
                console.log(err);
                setLoading(false);
            }
        );
    }, [id]);

    const handleTransferOwnership = async (selectedUser: any) => {
        if (!selectedUser || !id) {
            return;
        }

        setTransferLoading(true);
        try {
            const payload: { user_id?: string; email_address?: string } = {};
            if (selectedUser.user?.id) {
                payload.user_id = selectedUser.user.id;
            } else if (selectedUser.user?.email_address) {
                payload.email_address = selectedUser.user.email_address;
            } else {
                throw new Error("No valid user identifier found");
            }
            await transferFormOwnership(id, payload);
            setShowTransferModal(false);
        } catch (error) {
            console.error("Error transferring form ownership:", error);
        } finally {
            setTransferLoading(false);
        }
    };

    return (
        <div className={`gs-form-detail-page${loading ? " gs-form-detail-skeleton" : ""}`} aria-busy={loading || undefined}>
            {loading ? (
                <DetailHeaderSkeleton
                    titleClassName="gs-form-detail-skeleton-title"
                    actionClassName="gs-form-detail-skeleton-action"
                />
            ) : (
                <div className="gs-detail-header">
                    <h3 className="gs-detail-title" id="form-detail-subject">
                        {form?.name}
                    </h3>
                    {isTransferFormOwnershipPermitted && currentOrganization?.name !== GLOBAL_ORGANIZATION ? (
                        <TransferOwnershipButton
                            id="form-detail-transfer-ownership-btn"
                            iconId="form-detail-transfer-ownership-icon"
                            onClick={() => setShowTransferModal(true)}
                        />
                    ) : null}
                </div>
            )}

            {loading ? (
                <DetailTabsSkeleton tabWidths={FORM_DETAIL_TAB_WIDTHS} className="gs-page-tabs" />
            ) : (
                <ul className="gs-page-tabs">
                    <li>
                        <button
                            type="button"
                            className={`gs-page-tab${activeTab === "envelopes" ? " gs-page-tab--active" : ""}`}
                            id="form-detail-envelope-tab"
                            onClick={() => setActiveTab("envelopes")}
                        >
                            {HEADINGS.ENVELOPES}
                        </button>
                    </li>
                    <li>
                        <button
                            type="button"
                            className={`gs-page-tab${activeTab === "urls" ? " gs-page-tab--active" : ""}`}
                            id="form-detail-url-tab"
                            onClick={() => setActiveTab("urls")}
                        >
                            {HEADINGS.URLS}
                        </button>
                    </li>
                </ul>
            )}

            <div className="gs-form-detail-tab-panel">
                {activeTab === "envelopes" && id ? <FormEnvelopes id={id} /> : null}
                {!loading && activeTab === "urls" ? <FormURLsCard form={form} title="Form" /> : null}
            </div>

            {!loading ? (
                <TransferOwnershipModal
                    show={showTransferModal}
                    onHide={() => setShowTransferModal(false)}
                    onTransferOwnership={handleTransferOwnership}
                    loading={transferLoading}
                    currentOwnerId={form?.owner?.id}
                    title={LABELS.TRANSFER_FORM_OWNERSHIP}
                    description={LABELS.TRANSFER_FORM_OWNERSHIP_DESCRIPTION}
                />
            ) : null}
        </div>
    );
};

export default FormDetails;
